import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AppsIcon from '@mui/icons-material/Apps';
import { TOOLS_CONFIG } from '../../Setup/Config/tools-config';
import { setModal } from '../../Modules/Common/App';

// Styled Components
const StyledSearchField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '0.875rem',
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
        '& fieldset': {
            borderColor: theme.palette.divider,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.text.secondary,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1.5),
    },
}));

const MobileSearchField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        fontSize: '1.125rem',
        backgroundColor: theme.palette.background.paper,
        '& fieldset': {
            borderColor: theme.palette.divider,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.text.secondary,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
        },
    },
}));

const DropdownPaper = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: theme.spacing(0.5),
    maxHeight: '400px',
    zIndex: 1300,
    boxShadow: theme.shadows[8],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const ScrollableList = styled(Box)({
    overflowY: 'auto',
    flex: 1,
    maxHeight: '320px',
});

// Shared utility functions
const getDefaultTools = (defaultResults) => {
    return TOOLS_CONFIG
        .filter(tool => tool.available)
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, defaultResults);
};

const getFilteredTools = (query, maxResults) => {
    if (query.trim().length < 3) return [];

    const queryLower = query.toLowerCase().trim();
    return TOOLS_CONFIG
        .filter(tool =>
            tool.title.toLowerCase().includes(queryLower) ||
            tool.description.toLowerCase().includes(queryLower) ||
            tool.category.toLowerCase().includes(queryLower) ||
            tool.id.toLowerCase().includes(queryLower)
        )
        .filter(tool => tool.available)
        .sort((a, b) => a.title.localeCompare(b.title))
        .slice(0, maxResults);
};

const navigateToCatalog = (navigate, query) => {
    if (query?.trim()) {
        navigate(`/catalog?q=${encodeURIComponent(query.trim())}`);
    } else {
        navigate('/catalog');
    }
};

// Tool List Item Component (reusable)
const ToolListItem = ({ tool, onClick, theme, isMobile = false }) => {
    const IconComponent = tool.icon;
    return (
        <ListItem disablePadding sx={isMobile ? { mb: 0.5 } : {}}>
            <ListItemButton
                onClick={() => onClick(tool)}
                disabled={!tool.available}
                sx={{
                    borderRadius: theme.spacing(isMobile ? 1 : 0.5),
                    py: isMobile ? 1.5 : 1,
                    px: isMobile ? 2 : 2,
                    '&:hover': {
                        backgroundColor: isMobile
                            ? theme.palette.action.hover
                            : alpha(theme.palette.primary.main, 0.08),
                    },
                }}
            >
                <ListItemIcon sx={{ minWidth: isMobile ? 48 : 40 }}>
                    <IconComponent
                        sx={{
                            fontSize: isMobile ? '1.5rem' : '1.25rem',
                            color: tool.available ? theme.palette.primary.main : theme.palette.text.disabled
                        }}
                    />
                </ListItemIcon>
                <ListItemText
                    primary={tool.title}
                    secondary={tool.description}
                    primaryTypographyProps={{
                        fontSize: isMobile ? '1rem' : '0.875rem',
                        fontWeight: isMobile ? theme.typography.fontWeightMedium : 500,
                    }}
                    secondaryTypographyProps={{
                        fontSize: isMobile ? '0.875rem' : '0.75rem',
                        sx: isMobile ? {
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        } : {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }
                    }}
                />
            </ListItemButton>
        </ListItem>
    );
};

// Mobile Menu Content Component
const SearchMenuContent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const maxResults = 10;
    const defaultResults = 4;

    useEffect(() => {
        setTimeout(() => searchInputRef.current?.focus(), 100);
    }, []);

    const defaultTools = useMemo(() => getDefaultTools(defaultResults), [defaultResults]);
    const filteredTools = useMemo(() => getFilteredTools(searchQuery, maxResults), [searchQuery, maxResults]);
    const displayTools = searchQuery.trim().length >= 3 ? filteredTools : defaultTools;
    const hasResults = displayTools.length > 0;

    const handleClose = () => {
        dispatch(setModal({ component: null, open: false }));
    };

    const handleToolClick = (tool) => {
        if (tool.available) {
            navigate(tool.route);
            handleClose();
        }
    };

    const handleSearchSubmit = () => {
        navigateToCatalog(navigate, searchQuery);
        handleClose();
    };

    const handleShowCatalog = () => {
        navigateToCatalog(navigate, searchQuery);
        handleClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchSubmit();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <MobileSearchField
                        fullWidth
                        inputRef={searchInputRef}
                        placeholder="Search tools..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <IconButton onClick={handleClose} edge="end" aria-label="close search" sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {hasResults ? (
                    <>
                        <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                            <List>
                                {displayTools.map((tool) => (
                                    <ToolListItem key={tool.id} tool={tool} onClick={handleToolClick} theme={theme} isMobile />
                                ))}
                            </List>
                        </Box>
                        {searchQuery.trim().length >= 3 && (
                            <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 1 }}>
                                <ListItemButton
                                    onClick={handleShowCatalog}
                                    sx={{
                                        borderRadius: theme.spacing(1),
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                        },
                                    }}
                                >
                                    <ListItemIcon>
                                        <AppsIcon sx={{ color: theme.palette.primary.main }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Show Catalog"
                                        primaryTypographyProps={{
                                            fontWeight: theme.typography.fontWeightBold,
                                            color: 'primary.main',
                                        }}
                                    />
                                </ListItemButton>
                            </Box>
                        )}
                    </>
                ) : searchQuery.trim().length >= 3 ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No tools found matching "{searchQuery}"
                        </Typography>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

// Main SearchMenu Component
export const SearchMenu = ({
    placeholder = "Search tools...",
    width = '300px',
    minWidth = '200px',
    maxResults = 7,
    defaultResults = 4
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const searchBoxRef = useRef(null);

    const defaultTools = useMemo(() => getDefaultTools(defaultResults), [defaultResults]);
    const filteredTools = useMemo(() => getFilteredTools(searchQuery, maxResults), [searchQuery, maxResults]);
    const displayTools = searchQuery.trim().length >= 3 ? filteredTools : defaultTools;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        if (isFocused) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isFocused]);

    const handleToolClick = (tool) => {
        if (tool.available) {
            setIsFocused(false);
            setSearchQuery('');
            navigate(tool.route);
        }
    };

    const handleSearchSubmit = () => {
        setIsFocused(false);
        navigateToCatalog(navigate, searchQuery);
        setSearchQuery('');
    };

    const handleShowCatalog = () => {
        setIsFocused(false);
        navigateToCatalog(navigate, searchQuery);
        setSearchQuery('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchSubmit();
        }
    };

    const handleMobileClick = () => {
        dispatch(setModal({
            component: <SearchMenuContent />,
            open: true,
        }));
    };

    return (
        <>
            {/* Desktop Search Box */}
            <Box
                ref={searchBoxRef}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width,
                    minWidth,
                    position: 'relative',
                }}
            >
                <StyledSearchField
                    fullWidth
                    size="small"
                    placeholder={placeholder}
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsFocused(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsFocused(true)}
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                    }}
                    inputProps={{
                        autoComplete: 'off',
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                    }}
                />

                {isFocused && (
                    <DropdownPaper>
                        {displayTools.length > 0 && (
                            <ScrollableList>
                                <List dense sx={{ py: 0.5 }}>
                                    {displayTools.map((tool) => (
                                        <ToolListItem key={tool.id} tool={tool} onClick={handleToolClick} theme={theme} />
                                    ))}
                                </List>
                            </ScrollableList>
                        )}
                        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 1.5, backgroundColor: theme.palette.background.paper, flexShrink: 0 }}>
                            <ListItemButton
                                onClick={handleShowCatalog}
                                sx={{
                                    borderRadius: theme.spacing(0.5),
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <AppsIcon sx={{ fontSize: '1.25rem', color: theme.palette.primary.main }} />
                                </ListItemIcon>
                                <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                                    Show Catalog
                                </Typography>
                            </ListItemButton>
                        </Box>
                    </DropdownPaper>
                )}
            </Box>

            {/* Mobile Search Icon */}
            <IconButton
                onClick={handleMobileClick}
                aria-label="Search tools"
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    width: 40,
                    height: 40,
                    color: 'text.secondary',
                    border: '2px solid',
                    borderColor: 'text.secondary',
                    borderRadius: '50%',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'text.primary',
                        color: 'text.primary',
                    },
                }}
            >
                <SearchIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
        </>
    );
};

